import React, { useState, useEffect, useRef } from "react";
import resourcesData from "../data/resources";
import "./Resources.css";

/* ─── IntersectionObserver フック ─────────────────────────────── */
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, visible];
}

/* ─── カテゴリアイコン ─────────────────────────────────────────── */
const CategoryIcon = ({ category }) => {
  const icons = {
    "技術資料": (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18"/>
      </svg>
    ),
    "業界情報": (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
      </svg>
    ),
    "社内資料": (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
    "規格・基準": (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
      </svg>
    ),
  };
  return <span className="res-cat-icon">{icons[category] || icons["技術資料"]}</span>;
};

/* ─── ファイルタイプバッジ ──────────────────────────────────────── */
const TypeBadge = ({ type }) => {
  const map = { svg: "SVG", pdf: "PDF", image: "IMAGE", link: "LINK" };
  return <span className={`res-type-badge res-type-${type}`}>{map[type] || type.toUpperCase()}</span>;
};

/* ─── モーダル ─────────────────────────────────────────────────── */
function ResourceModal({ resource, onClose }) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKey);
    };
  }, [onClose]);

  return (
    <div className="res-modal-backdrop" onClick={onClose}>
      <div className="res-modal" onClick={(e) => e.stopPropagation()}>
        {/* ヘッダー */}
        <div className="res-modal-header">
          <div className="res-modal-meta">
            <CategoryIcon category={resource.category} />
            <span className="res-modal-category">{resource.category}</span>
            <TypeBadge type={resource.type} />
          </div>
          <button className="res-modal-close" onClick={onClose} aria-label="閉じる">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="res-modal-body">
          {/* タイトルエリア */}
          <div className="res-modal-title-area">
            <h2 className="res-modal-title">{resource.title}</h2>
            {resource.subtitle && <p className="res-modal-subtitle">{resource.subtitle}</p>}
            <p className="res-modal-desc">{resource.description}</p>
            <div className="res-modal-tags">
              {resource.tags.map((tag) => (
                <span key={tag} className="res-tag">#{tag}</span>
              ))}
            </div>
          </div>

          {/* コンテンツビューア */}
          <div className="res-modal-viewer">
            {resource.type === "svg" && (
              <div className="res-svg-viewer">
                <div className="res-svg-scroll">
                  <img
                    src={resource.file}
                    alt={resource.title}
                    className="res-svg-img"
                  />
                </div>
                <p className="res-viewer-hint">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                    <polyline points="7 13 12 18 17 13"/><polyline points="7 6 12 11 17 6"/>
                  </svg>
                  スクロールして全体を確認できます
                </p>
              </div>
            )}
            {resource.type === "pdf" && (
              <div className="res-pdf-placeholder">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
                <a href={resource.file} target="_blank" rel="noreferrer" className="res-open-btn">
                  PDFを開く
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── リソースカード ────────────────────────────────────────────── */
function ResourceCard({ resource, index, onClick }) {
  const [ref, visible] = useReveal();
  const [hover, setHover] = useState(false);

  return (
    <div
      ref={ref}
      className={`res-card-wrap ${visible ? "is-visible" : ""}`}
      style={{ "--delay": `${index * 0.07}s` }}
    >
      <button
        className={`res-card ${hover ? "is-hovered" : ""}`}
        onClick={() => onClick(resource)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        aria-label={`${resource.title}を開く`}
      >
        {/* 左：カテゴリアイコン帯 */}
        <div className="res-card-accent">
          <CategoryIcon category={resource.category} />
        </div>

        {/* 中央：本文 */}
        <div className="res-card-body">
          <div className="res-card-meta">
            <span className="res-card-category">{resource.category}</span>
            <TypeBadge type={resource.type} />
            <time className="res-card-date">{resource.date}</time>
          </div>
          <h3 className="res-card-title">{resource.title}</h3>
          {resource.subtitle && (
            <p className="res-card-subtitle">{resource.subtitle}</p>
          )}
          <p className="res-card-desc">{resource.description}</p>
          <div className="res-card-tags">
            {resource.tags.map((tag) => (
              <span key={tag} className="res-tag"># {tag}</span>
            ))}
          </div>
        </div>

        {/* 右：矢印 */}
        <div className="res-card-arrow">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="5" y1="12" x2="19" y2="12"/>
            <polyline points="12 5 19 12 12 19"/>
          </svg>
        </div>

        <div className="res-card-shine" />
      </button>
    </div>
  );
}

/* ─── メインページ ─────────────────────────────────────────────── */
export default function ResourcesList() {
  const [heroRef, heroVisible] = useReveal();
  const [activeModal, setActiveModal] = useState(null);
  const [activeCategory, setActiveCategory] = useState("すべて");

  const categories = ["すべて", ...Array.from(new Set(resourcesData.map((r) => r.category)))];

  const filtered =
    activeCategory === "すべて"
      ? resourcesData
      : resourcesData.filter((r) => r.category === activeCategory);

  return (
    <div className="res-page">
      <div id="page-top" />

      {/* ── ヒーロー ── */}
      <div ref={heroRef} className={`res-hero ${heroVisible ? "is-visible" : ""}`}>
        <div className="res-hero-bg">
          <div className="res-hero-circuit" />
        </div>

        <div className="res-hero-content">
          <p className="res-hero-eyebrow">ZENKYO RESOURCES</p>
          <h1 className="res-hero-title">
            <span className="res-hero-title-ja">資料・情報</span>
            <span className="res-hero-title-slash">/</span>
            <span className="res-hero-title-en">Library</span>
          </h1>
          <p className="res-hero-desc">
            半導体・ウェーハに関する技術資料や業界情報など、<br />
            お役立ちコンテンツをまとめています。
          </p>
          <div className="res-hero-stats">
            <div className="res-stat">
              <span className="res-stat-num">{resourcesData.length}</span>
              <span className="res-stat-label">資料</span>
            </div>
            <div className="res-stat-divider" />
            <div className="res-stat">
              <span className="res-stat-num">{categories.length - 1}</span>
              <span className="res-stat-label">カテゴリ</span>
            </div>
          </div>
        </div>

        {/* デコレーション：ウェーハリング */}
        <div className="res-hero-deco" aria-hidden="true">
          <div className="res-wafer-ring res-wafer-ring-1" />
          <div className="res-wafer-ring res-wafer-ring-2" />
          <div className="res-wafer-ring res-wafer-ring-3" />
          <div className="res-wafer-dot" />
        </div>
      </div>

      {/* ── フィルターバー ── */}
      <div className="res-filter-bar">
        <div className="res-filter-inner">
          <span className="res-filter-label">カテゴリ</span>
          <div className="res-filter-pills">
            {categories.map((cat) => (
              <button
                key={cat}
                className={`res-pill ${activeCategory === cat ? "is-active" : ""}`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
                <span className="res-pill-count">
                  {cat === "すべて"
                    ? resourcesData.length
                    : resourcesData.filter((r) => r.category === cat).length}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── カードリスト ── */}
      <div className="res-list-section">
        <div className="res-list">
          {filtered.map((resource, index) => (
            <ResourceCard
              key={resource.id}
              resource={resource}
              index={index}
              onClick={setActiveModal}
            />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="res-empty">
            <p>該当する資料がありません</p>
          </div>
        )}
      </div>

      {/* ── モーダル ── */}
      {activeModal && (
        <ResourceModal
          resource={activeModal}
          onClose={() => setActiveModal(null)}
        />
      )}
    </div>
  );
}
